<template>
    <local-grid ref="roleRef" class="h-full" :meta="meta" :data-source="state.userRequestBody.roles">
        <template #toolbarItems>
            <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                <dx-button
                    class="ai-button-base"
                    hint="Добавить роль"
                    @click="showPopup"
                    :disabled="isToolbarElementsDisabled"
                >
                    <ai-add-base />
                </dx-button>
            </dx-item>
            <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                <dx-button
                    class="ai-button-base"
                    hint="Удалить роль"
                    @click="deleteRole"
                    :disabled="isToolbarElementsDisabled || disabled"
                >
                    <ai-delete-base />
                </dx-button>
            </dx-item>
        </template>
        <template #restrictions="{ data }">
            <div class="flex items-center justify-between">
                <div class="w-[92%] mr-2">
                    {{ data.data.description }}
                </div>
                <div class="justify-left">
                    <dx-button
                        :icon="AiEllipsis"
                        class="button-ellipsis cursor-pointer"
                        width="20px"
                        hint="Просмотр областей и типов доступа в роли"
                        @click="showRoleDescription(data)"
                    />
                </div>
            </div>
        </template>
    </local-grid>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { DxItem } from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue';
import LocalGrid from '@/components/local-grid.vue';
import usersLocalStore from '@/views/administration/users/store.js';
import rolesStore from '@/store/administration/roles.js';
import environmentStore from '@/store/enviroment-data.js';
import AiEllipsis from '@/assets/ai-ellipsis.svg';

const { UI_ELEMENTS_IDS, state, loadById } = usersLocalStore;
const { getAreaList, getGroupList, getAreaListWithApps } = rolesStore;
const { disableUiElement } = environmentStore;

const isToolbarElementsDisabled = disableUiElement(UI_ELEMENTS_IDS.ROLES);
const roleRef = ref();
const gridInstance = ref(null);
const disabled = ref(true);
const contentReadyHandled = ref();

onMounted(async () => {
    gridInstance.value = roleRef.value.gridInstance;
    gridInstance.value.on('selectionChanged', onSelectionChanged);
    gridInstance.value.on('optionChanged', onOptionChanged);
    contentReadyHandled.value = false;
    gridInstance.value.on('contentReady', onContentReady);
});

const onContentReady = async () => {
    if (contentReadyHandled.value) return;
    state.userRequestBody.roles.forEach(role => {
        if (role.deleted) {
            const index = gridInstance.value.getRowIndexByKey(role.id);
            gridInstance.value.deleteRow(index);
        }
    });
    contentReadyHandled.value = true;
};

const onSelectionChanged = event => {
    disabled.value = !event.selectedRowKeys.length;
};

const showRoleDescription = async event => {
    await getAreaList();
    await getAreaListWithApps();
    await getGroupList();

    const clickedRoleId = event.data.id;
    const clickedRole = state.userRequestBody.roles.find(role => role.id === clickedRoleId);
    const clickedAppId = clickedRole.areas[0].app_id;

    await loadById(clickedRoleId, clickedAppId);
    state.isClosingConfirmed = true;
    state.isOpenUserCard = false;
    state.usersRolePopupVisible = false;
    state.isOpenedByUsersRole = true;
    state.userCardPopupVisible = false;
    state.roleAreasPopupVisible = true;
};

const showPopup = () => {
    state.isClosingConfirmed = true;
    state.userCardPopupVisible = false;
    state.usersRolePopupVisible = true;
};

const deleteRole = () => {
    const selected = gridInstance.value.getSelectedRowKeys();
    state.userRequestBody.roles = state.userRequestBody.roles.filter(role => !selected.includes(role.id));
    gridInstance.value.deselectAll();
};

const onOptionChanged = event => {
    const { fullName, value } = event;
    if (fullName !== 'editing.changes') return;

    for (const obj of value) {
        if (obj.type === 'remove') {
            const removedRowId = obj.key;
            state.userRequestBody.roles.forEach(role => {
                if (role.id === removedRowId) {
                    role.deleted = true;
                }
            });
        }
    }
};

const meta = {
    keyExpr: 'id',
    wordWrapEnabled: true,
    scrollingMode: 'standart',
    allowSelectAll: true,
    selectionMode: 'multiple',
    columns: [
        {
            dataField: 'id',
            caption: 'ID',
            visible: false,
            dataType: 'number',
        },
        {
            dataField: 'code',
            caption: 'Код роли',
            dataType: 'string',
        },
        {
            dataField: 'name',
            caption: 'Роль',
            dataType: 'string',
        },
        {
            dataField: 'description',
            caption: 'Описание роли',
            cellTemplate: 'restrictions',
            dataType: 'string',
        },
        {
            dataField: 'apps_names',
            caption: 'Приложение, доступное роли',
            dataType: 'string',
        },
    ],
};
</script>

<style lang="scss" scoped>
:deep(.button-ellipsis.dx-button) {
    min-width: 0;
    min-height: 0;
    height: unset;
    display: flex;

    .dx-button-content {
        padding: 0;
        min-width: 0;
        min-height: 0;
    }

    .dx-icon {
        width: 20px;
        height: 20px;
        min-width: 0;
    }
}
</style>
